import React from 'react';
import PropTypes from 'prop-types';
import getStyles from '@front10/helpers/dist/UseStyles';

//-------------------------------------
// Basic components here
//-------------------------------------
import Header from '../../elements/Header';
import DatesRangeSelector from '../../controls/DatesRangeSelector';
import Row from '../../elements/Row';
import Col from '../../elements/Col';
import Box from '../../elements/Box';
import Text from '../../elements/Text';
import FareListTwoColumns from '../../lists/FareListTwoColumns';
import AirBnBMoreFilters from '../../controls/AirBnBMoreFilters';
import LocationSelector from '../../controls/LocationSelector';
import OfferHorizontalCard from '../../items/OfferHorizontalCard';
import FeaturedOfferCard from '../../items/FeaturedOfferCard';
import Disclaimer from '../../others/Disclaimer';
import Skeleton from '../../others/Skeleton/SkeletonFeaturedDestinations';
import Loading from '../../others/Loading';
import NoFeaturedPopularFaresAlert from '../../alerts/NoFeaturedPopularFaresAlert';
import ContainerErrorAlert from '../../alerts/ContainerErrorAlert';
import Button from '../../elements/Button';
import Icon from '../../elements/Icon';
import AirBnBIcon from '../../elements/AirBnbIcon';
import GuestSelector from '../../controls/GuestsSelector';
import withDeepMergeDefaultProps from '../../utils/WithDeepMergeDefaultProps';

// airbnb actions
import detailHouse from '../../../_integrations/airbnb/actions';

//-------------------------------------
// Every mundo integration
//-------------------------------------
import { getOffers } from '../../../_integrations/airbnb/listings';
import { getAirports as getCities } from '../../../_integrations/everymundo/airports';
import {
  hookFaresOnFilterChange,
  hookComponentDidMount,
  hookOnErrorAlert
} from '../_commons/featuredOffersHook';

//-------------------------------------
// Integration Layer Default Actions
//-------------------------------------
import useChangeOrigin from '../../../_integrations/everymundo/tracking/_experimental/events/useChangeOrigin';
import useChangeDateRange from '../../../_integrations/everymundo/tracking/_experimental/events/useChangeDateRange';
import useChangeBudget from '../../../_integrations/everymundo/tracking/_experimental/events/useChangeBudget';
import useChangeHomeType from '../../../_integrations/everymundo/tracking/_experimental/events/useChangeHomeType';
import useChangeGuests from '../../../_integrations/everymundo/tracking/_experimental/events/useChangeGuests';
import useClickListing from '../../../_integrations/everymundo/tracking/_experimental/events/useClickListing';

import { isEmpty } from '../../../helpers/utils';
import { computeDateRange, isSameDateRange } from '../../../helpers/date';
import { GuestsSelectorContainer } from '../../controls/GuestsSelector/commons';

const cardType = 'listing';

class AirBnBFeaturedDestinationsContainer extends React.Component {
  state = {
    originCityName: '',
    errorLoading: false,
    isLoadingDepartureAirports: false,
    isLoadingOffers: false,
    departureAirports: this.props.data.initialDepartureAirports || [],
    offers: this.props.data.initialOffers || [],
    isSkeletonShown: this.props.showSkeletonScreen,
    origin: this.props.prePopulation.origin || '',
    dateRange: this.props.prePopulation.dateRange
      ? computeDateRange(this.props.prePopulation.dateRange)
      : {},
    budget:
      this.props.prePopulation.budget && this.props.prePopulation.budget.maximum
        ? this.props.prePopulation.budget.maximum
        : null,
    homeType: this.props.prePopulation.homeType || [],
    guests:
      this.props.prePopulation.guests && !isEmpty(this.props.prePopulation.guests)
        ? this.props.prePopulation.guests.adults + this.props.prePopulation.guests.children
        : 0
  };

  header = (
    <Header
      content={
        this.props.components.title.content ||
        this.props.translate('cmp-featured-airbnb-container-title', 'TOP PLACES')
      }
      {...this.props.components.title}
    />
  );

  skeleton = (<Skeleton {...this.props.skeletonSettings} position="absolute" width="100" />);

  disclaimer = (<Disclaimer {...this.props.components.disclaimer} />);

  constructor(props) {
    super(props);
    this.dispatchGuests = React.createRef();
  }

  componentDidMount = () => {
    const { data, containerType } = this.props;
    if (containerType !== 'dumb') {
      // Preparing for the real hooks :)
      // we are using the "hook" concept here to reuse logic
      // between similar containers in case we need to
      if (data.initialOffers) {
        this.setState({
          isSkeletonShown: false
        });
      } else {
        hookComponentDidMount(this);
      }
    } else {
      hookComponentDidMount(this);
    }
  };

  renderAirport = (item) => {
    const { showCityCode } = this.props;
    const code = item.label ? item.label.substring(0, 3).toUpperCase() : '';
    return (
      <>
        {showCityCode && (
          <Box
            border
            paddingWidth="1"
            marginRight="2"
            floatLeft
            textAlign="center"
            fontWeight="bold"
            style={{ minWidth: '45px' }}
          >
            <span style={{ paddingTop: '2px' }}>{code}</span>
          </Box>
        )}
        <Box>
          <span>{item.label}</span>
        </Box>
      </>
    );
  };

  handleAirport = (origin) => {
    // Notifications via props (if Everymundo wants
    // to pass notifications they can do it via props)
    this.props.onChangeDepartureCity(this.props, origin.value, cardType);

    this.setState({ origin: origin.value }, () => {
      hookFaresOnFilterChange(this);
    });
  };

  clearAirport = () => {
    this.setState({ origin: '' }, () => {
      hookFaresOnFilterChange(this);
    });
  };

  tryAgain = () => hookOnErrorAlert(this);

  onChangeDate = ({ start, end }) => {
    if (!isSameDateRange({ start, end }, this.state.dateRange)) {
      // Notifications via props (if Everymundo wants
      // to pass notifications they can do it via props)
      this.props.onChangeDate(this.props, start, end, cardType);

      this.setState({ dateRange: { start, end } }, () => {
        hookFaresOnFilterChange(this);
      });
    }
  };

  onClearDate = () => {
    this.setState({ dateRange: {} }, () => {
      hookFaresOnFilterChange(this);
    });
  };

  onChangeMoreFilter = ({ budget, homeType }) => {
    // Notifications via props (if Everymundo wants
    // to pass notifications they can do it via props)
    if (this.state.budget !== budget) {
      this.props.onChangeBudget(this.props, budget, cardType);
    }
    if (this.state.homeType !== homeType) {
      this.props.onChangeHomeType(this.props, homeType);
    }
    this.setState({ budget, homeType }, () => {
      hookFaresOnFilterChange(this);
    });
  };

  onClearMoreFilter = () => {
    this.setState({ budget: null, homeType: null }, () => {
      hookFaresOnFilterChange(this);
    });
  };

  onChangeGuest = ({ adults, children }) => {
    const { guests } = this.state;
    const { onChangeGuests } = this.props;
    const newGuests = adults + children;
    if (newGuests !== guests) {
      onChangeGuests(this.props, newGuests);
      this.setState({ guests: newGuests }, () => {
        hookFaresOnFilterChange(this);
      });
    }
  };

  resetFilters = () => {
    this.setState(
      {
        origin: '',
        budget: null,
        homeType: null,
        dateRange: { start: undefined, end: undefined }
      },
      () => {
        this.datesRangeSelector.reset();
        this.moreFilters.reset();
        this.dispatchGuests.current({ type: 'CLEAR' });
        hookFaresOnFilterChange(this);
      }
    );
  };

  /**
   *
   * @param {*} props
   * @returns The settings destinationItem more the settings needed to
   * executed tracking and modal opened
   * @memberof FeaturedDestinationContainer
   */
  getDestinationItemTracking = () => {
    return { onClick: this.handleFareItemClick, callToActionOnClick: this.handleFareItemClick };
  };

  /**
   *
   * @param {*} props
   * @returns The settings fareTwoList more the settings needed to executed tracking
   * @memberof FeaturedDestinationContainer
   */
  getFareTwoListTracking = () => {
    const {
      components: { featuredOfferItem }
    } = this.props;
    return {
      itemSettings: {
        ...featuredOfferItem,
        onClick: this.handleFareItemClick,
        callToActionOnClick: this.handleFareItemClick
      }
    };
  };

  handleFareItemClick = ({ data }) => {
    // Notifications via props (if Everymundo wants
    // to pass notifications they can do it via props)*/
    this.props.fareItemOnClickAction(this.props, data, this.state.dateRange);
  };

  render() {
    const {
      departureAirports,
      offers,
      isSkeletonShown,
      origin,
      isLoadingOffers,
      originCityName,
      errorLoading,
      isLoadingDepartureAirports,
      dateRange,
      budget,
      homeType,
      guests
    } = this.state;
    const {
      components,
      translate,
      showSkeletonScreen,
      showLoading,
      showFilters,
      showTitle,
      showDisclaimer,
      prePopulation,
      direction,
      theme,
      showSecondaryTitle
    } = this.props;
    const [className, style] = getStyles(this.props, { direction, theme });

    const featuredOffers = offers.slice(1);
    const mainFeaturedOffer = offers[0];
    const mainFeaturedOfferCol = featuredOffers.length
      ? components.mainFeaturedOfferCol
      : { ...components.mainFeaturedOfferCol, lg: '12' };
    const featuredOffersCol = { lg: 12 - mainFeaturedOfferCol.lg, ...components.featuredOffersCol };
    const myStyle = Object.assign({ minHeight: '600px' }, style);
    const isFiltered =
      origin !== '' ||
      (dateRange.start && dateRange.end) ||
      (budget && budget !== '') ||
      (homeType && homeType.length > 0) ||
      (guests !== null && guests !== undefined && guests > 0);
    const defaultGuests =
      prePopulation.guests &&
      typeof prePopulation.guests === 'object' &&
      Object.keys(prePopulation.guests).length > 0
        ? prePopulation.guests
        : { adults: 0, children: 0, infants: 0 };

    const trackingDestinationItem = this.getDestinationItemTracking();
    const listItemsSettings = this.getFareTwoListTracking();

    const renderContent = (
      <Box
        position="relative"
        className={`${AirBnBFeaturedDestinationsContainer.componentName} ${className}`}
        style={myStyle}
        dataTestId="AirBnBFeaturedDestinationsContainer"
      >
        {isLoadingOffers && showLoading && <Loading {...components.loading} />}
        {showSkeletonScreen && isSkeletonShown && this.skeleton}
        {showTitle && (
          <Row gutters>
            <Col {...components.titleContainer}>
              <AirBnBIcon {...components.airBnBIconStyles} />
              <Text paddingWidth="1">{this.header}</Text>
            </Col>
          </Row>
        )}
        {showSecondaryTitle && (
          <Row gutters>
            <Col {...components.titleContainer}>
              <AirBnBIcon {...components.airBnBIconStyles} />
              <Text paddingWidth="1">Demo title secondary</Text>
            </Col>
          </Row>
        )}
        {showFilters && (
          <Row {...components.filterContentSettings} gutters>
            <Col lg="12" textAlign="right">
              <Button
                paddingWidth="0"
                textTransform="uppercase"
                fontSize="3"
                {...components.clearAll}
                onClick={this.resetFilters}
                disabled={!isFiltered}
                dataTestId="clearAllBtn"
                display="inline-flex"
                alignItems="center"
              >
                {translate('cmp-filter-clear', 'Clear all')}
                <Icon icon="close" paddingLeft="1" size={18} {...components.clearAll.icon} />
              </Button>
            </Col>
            <Col lg="3" paddingTop="2" className="fr-pt-lg-0">
              <LocationSelector
                {...components.locationSelector}
                onChange={this.handleAirport}
                options={departureAirports}
                showCode={false}
                placeholder={translate(
                  'cmp-location-destination-placeholder',
                  'Select Departure City'
                )}
                itemRenderer={this.renderAirport}
                defaultValue={origin}
                onClear={this.clearAirport}
                loading={isLoadingDepartureAirports}
              />
            </Col>
            <Col paddingTop="2" className="fr-pt-lg-0">
              <DatesRangeSelector
                {...components.datesRangeSelector}
                defaultValue={dateRange}
                onChange={this.onChangeDate}
                onClear={this.onClearDate}
                departInputPlaceholderLabelKey="cmp-airbnb-date-date-range-from-placeholder"
                returnInputPlaceholderLabelKey="cmp-airbnb-date-date-range-to-placeholder"
                focusReferences={(ref) => {
                  this.datesRangeSelector = ref;
                }}
              />
            </Col>
            <Col lg="2" paddingTop="2" className="fr-pt-lg-0">
              <GuestsSelectorContainer initialValue={defaultGuests}>
                {([newGuests, dispatch]) => {
                  this.dispatchGuests.current = dispatch; // Escape hatch to sort the scope limitation of the render prop
                  return (
                    <GuestSelector
                      {...components.guestsSelector}
                      value={newGuests}
                      onApply={this.onChangeGuest}
                      dispatch={dispatch}
                      focusReferences={(ref) => {
                        this.guest = ref;
                      }}
                    />
                  );
                }}
              </GuestsSelectorContainer>
            </Col>
            <Col lg="3" paddingTop="2" className="fr-pt-lg-0">
              <AirBnBMoreFilters
                {...components.moreFilter}
                onChange={this.onChangeMoreFilter}
                defaultBudget={budget}
                defaultHomeType={homeType}
                focusReferences={(ref) => {
                  this.moreFilters = ref;
                }}
              />
            </Col>
          </Row>
        )}
        <Row {...components.mainContentSettings} gutters>
          {errorLoading && (
            <Col>
              <ContainerErrorAlert
                tryAgain={this.tryAgain}
                labelKeySubTitleIntro="cmp-airbnb-error-reload-intro"
                labelKeySubTitle="cmp-text-tryagain"
                {...components.alert}
              />
            </Col>
          )}
          {offers.length === 0 && !errorLoading && (
            <Col>
              <NoFeaturedPopularFaresAlert
                labelKeyTitle="cmp-alert-no-fares-available"
                labelKeyLink="cmp-airbnb-alert-link-more-homes"
                showLink
                onLinkClick={detailHouse}
                {...components.noResultsPlaceHolder}
              />
            </Col>
          )}
          {!errorLoading && offers.length !== 0 && (
            <>
              {mainFeaturedOffer && (
                <Col {...mainFeaturedOfferCol}>
                  <FeaturedOfferCard
                    {...components.mainFeaturedOffer}
                    showCityName={false}
                    image={mainFeaturedOffer.destinationImage}
                    data={mainFeaturedOffer}
                    {...trackingDestinationItem}
                  />
                </Col>
              )}
              {featuredOffers.length !== 0 && (
                <Col {...featuredOffersCol}>
                  <Box {...components.featuredOffersTitleLabelContainer}>
                    <Text {...components.featuredOffersTitleLabel} type="span">
                      {translate(
                        'cmp-featured-airbnb-container-other-locations',
                        'Other popular homes'
                      )}
                    </Text>
                    {origin && originCityName && (
                      <Text {...components.featuredOffersTitleValue} type="span">
                        {`: ${originCityName}`}
                      </Text>
                    )}
                  </Box>
                  <FareListTwoColumns
                    ItemComponentInstance={OfferHorizontalCard}
                    itemsData={featuredOffers}
                    {...components.featuredOffersList}
                    {...listItemsSettings}
                  />
                </Col>
              )}
            </>
          )}
        </Row>
        {showDisclaimer && (
          <Row {...components.disclaimerContentSettings}>
            <Col>{this.disclaimer}</Col>
          </Row>
        )}
      </Box>
    );

    return renderContent;
  }
}

AirBnBFeaturedDestinationsContainer.propTypes = {
  /**
   * To be used to set if the container is a controlled one
   */
  containerType: PropTypes.string,
  /**
   * ClassName of the components
   */
  className: PropTypes.string,
  /*
   * Action to be executed when clicking in fareItem
   */
  fareItemOnClickAction: PropTypes.func,
  /**
   * Function to getFares
   */
  getOffers: PropTypes.func,
  /**
   * Function for request to obtain airports
   */
  getCities: PropTypes.func,
  /**
   * Component Behavior when there are not results available
   */
  noFaresBehavior: PropTypes.oneOf(['hidden', 'showNoResults']),
  /**
   * Handle the action when change departure city
   */
  onChangeDepartureCity: PropTypes.func,
  /**
   * Handle the action when change date
   */
  onChangeDate: PropTypes.func,
  /**
   * Handle the action when change budget price
   */
  onChangeBudget: PropTypes.func,
  /**
   * Handle the action when change home type
   */
  onChangeHomeType: PropTypes.func,
  /**
   * Page Context
   */
  pageContext: PropTypes.shape({
    deviceCategory: PropTypes.string,
    siteEdition: PropTypes.string
  }),
  /**
   * Settings of the Pagination
   */
  paginationSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Component PrePopulation
   */
  prePopulation: PropTypes.objectOf(PropTypes.any),
  /**
   * Determine if the component will show the disclaimer
   */
  showDisclaimer: PropTypes.bool,
  /**
   * Shows Loading Component
   */
  showLoading: PropTypes.bool,
  /**
   * Determine if the component will show the skeleton screen while
   * loading (Default value will be false)
   */
  showSkeletonScreen: PropTypes.bool,
  /**
   * Determine if the component will show the filters.
   */
  showFilters: PropTypes.bool,
  /**
   * Determine if the component will show the title
   */
  showTitle: PropTypes.bool,
  /**
   * Css style applied to the component
   */
  style: PropTypes.objectOf(PropTypes.any),
  /**
   * SkeletonSettings Component
   */
  skeletonSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Props to translate
   */
  translate: PropTypes.func,
  /**
   * Sub-Components settings
   */
  components: PropTypes.objectOf(PropTypes.any),
  /**
   * Container real-time data
   */
  data: PropTypes.objectOf(PropTypes.any),
  /**
   * Define if iata code in airport is show
   */
  showCityCode: PropTypes.bool,
  /**
   * Define if always show the total price
   */
  alwaysShowTotalPrice: PropTypes.bool,
  /**
   * Direction of the component.
   */
  direction: PropTypes.oneOf(['rtl', 'ltr']),
  /**
   * Possible interactions will be Popup or Direct Deeplink
   */
  fareItemInteractionType: PropTypes.oneOf(['popup', 'deeplink']),
  /**
   * LocalizationData via moment to be used as a date translator
   * https://github.com/moment/moment/blob/develop/src/locale/es.js
   */
  localizationData: PropTypes.objectOf(PropTypes.any),
  /**
   * Theme object on the component
   */
  theme: PropTypes.objectOf(PropTypes.any),
  /**
   * Handle the action when change guest
   */
  onChangeGuests: PropTypes.func,
  /**
   * Parameters tracking
   */
  tracking: PropTypes.objectOf(PropTypes.any)
};

AirBnBFeaturedDestinationsContainer.defaultProps = {
  fareItemInteractionType: 'popup',
  showDisclaimer: true,
  showLoading: true,
  showSkeletonScreen: true,
  showFilters: true,
  showTitle: true,
  components: {
    moreFilter: {
      shadow: '@theme:input.shadow',
      rounded: '@theme:input.rounded'
    },
    airBnBIconStyles: {},
    title: {
      fontSize: '6',
      margin: '0',
      textAlign: 'left'
    },
    titleContainer: {
      display: 'inline-flex',
      alignItems: 'center'
    },
    mainFeaturedOffer: {
      callToActionOnClick: detailHouse
    },
    featuredOffersTitleLabelContainer: {
      marginBottom: '2',
      style: { lineHeight: '1' }
    },
    mainFeaturedOfferCol: {
      lg: '8'
    },
    featuredOfferItem: {
      onClick: detailHouse,
      callToActionOnClick: detailHouse
    },
    featuredOffersList: {},
    featuredOffersCol: {},
    alert: {
      bgColor: 'light',
      textAlign: 'center',
      padding: '3',
      rounded: '@theme:card.rounded'
    },
    clearAll: {
      color: 'link',
      bgColor: 'transparent',
      textColor: 'light-18',
      borderNone: true,
      icon: {
        iconColor: 'light-18'
      }
    },
    disclaimer: {
      labelKey: 'cmp-featured-listings-container-disclaimer'
    },
    disclaimerContentSettings: {
      marginTop: '2',
      fontSize: '11px'
    },
    filterContentSettings: {},
    mainContentSettings: {
      marginTop: '3'
    },
    noResultsPlaceHolder: {
      bgColor: 'light',
      rounded: '@theme:card.rounded'
    },
    guestsSelector: {
      shadow: '@theme:input.shadow',
      rounded: '@theme:input.rounded'
    },
    loading: {}
  },
  data: {
    initialDepartureAirports: null,
    initialOffers: null
  },
  containerType: 'stateful',
  noFaresBehavior: 'hidden',
  className: '',
  getOffers,
  getCities,
  onChangeDepartureCity: useChangeOrigin,
  onChangeDate: useChangeDateRange,
  onChangeBudget: useChangeBudget,
  onChangeHomeType: useChangeHomeType,
  fareItemOnClickAction: useClickListing,
  onChangeGuests: useChangeGuests,
  pageContext: {
    deviceCategory: 'DESKTOP',
    siteEdition: 'en_HK'
  },
  paginationSettings: {
    type: 'load_more',
    step: 10,
    limit: 30,
    intialVisibleItems: 10
  },
  prePopulation: {},
  style: {},
  skeletonSettings: {
    style: { backgroundColor: 'white' }
  },
  translate: () => {},
  showCityCode: true,
  alwaysShowTotalPrice: false,
  direction: 'ltr',
  localizationData: null,
  theme: null,
  tracking: {
    flexibleDates: false,
    passengerCount: 1,
    promoCode: 'EVERYMUNDO',
    eventType: 'listingsFeaturedBackgroundImage'
  }
};
AirBnBFeaturedDestinationsContainer.componentName = 'AirBnBFeaturedDestinationsContainer';

export default withDeepMergeDefaultProps(AirBnBFeaturedDestinationsContainer);
